/**
 * GuidedTourWelcomeOverlay — full-screen welcome overlay for the unified
 * guided-tour system.
 *
 * Welcome overlay for the guided tour system. Shows a grid of flow cards with summaries,
 * step counts, and audio-ready badges. When a flow is selected, shows a detail
 * view with summary TTS playback and action buttons:
 * - "Show me now" → engine.selectFlow(flow.id, 'interactive')
 * - "Continue from step X" → engine.selectFlow(flow.id, 'interactive', stepId)
 * - "Let's chat about it" → engine.selectChat(flow.id)
 *
 * The mascot is shown on the right panel (desktop) or hidden (mobile).
 *
 * @created September 2026 — Phase 5 of the tour consolidation.
 */

'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft,
  ChevronRight,
  Play,
  MessageSquare,
  Minimize2,
  RotateCcw,
  Sparkles,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import { useGuidedTour } from './GuidedTourProvider';
import { useTourTTS } from './hooks/useTourTTS';
import type { TourFlow } from '@/config/guided-tours/types';

// Static fallback mascot image (same as TourChatPanel)
const FALLBACK_MASCOT_URL =
  'https://staticfileserver.totalaccess.co.za/files/628cab51c28f5e5bf0b55242306ef160314ca93c78b2c3cb5da6e9cd6ccef9e5.png';

export function GuidedTourWelcomeOverlay() {
  const tour = useGuidedTour();
  const { engine, config, moduleKey, closeTour, flows, progress, openTour } = tour;

  const schema = config?.schema ?? null;
  const moduleColor = '#dc2626';
  const moduleLabel = config?.moduleConfig?.module_name ?? moduleKey;
  const isDark = typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches;

  const [selectedFlow, setSelectedFlow] = useState<TourFlow | null>(null);
  const [summaryPlaying, setSummaryPlaying] = useState(false);
  const playingFlowRef = useRef<string | null>(null);

  // ── TTS for summary narration ──────────────────────────────────────────
  const tts = useTourTTS({
    currentStepId: selectedFlow?.id ?? null,
    currentNarrationText: selectedFlow?.view_summary ?? selectedFlow?.summary ?? '',
    currentAudioUrl: selectedFlow?.summary_audio_url ?? null,
    isOpen: tour.isOpen,
    isMinimized: engine.isMinimized,
    voice: schema?.tts?.voice ?? schema?.audio?.default_voice ?? 'af_heart',
    autoPlay: false,
  });

  // Auto-play summary when a flow is selected (if TTS is enabled)
  useEffect(() => {
    if (!selectedFlow || !tour.isOpen) return;
    if (!tts.enabled) return;

    setSummaryPlaying(true);
    playingFlowRef.current = selectedFlow.id;
    // Use the play() method which handles pre-generated → on-demand fallback
    // We need a slight delay to let the hook pick up the new currentStepId
    const timer = setTimeout(() => {
      tts.play();
    }, 50);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFlow, tour.isOpen]);

  // Track when audio finishes playing
  useEffect(() => {
    if (!tts.isPlaying && !tts.isLoading && summaryPlaying) {
      setSummaryPlaying(false);
      playingFlowRef.current = null;
    }
  }, [tts.isPlaying, tts.isLoading, summaryPlaying]);

  // Stop TTS when overlay closes or flow deselects
  useEffect(() => {
    if (!tour.isOpen || engine.viewMode !== 'index') {
      tts.stop();
      setSummaryPlaying(false);
      playingFlowRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour.isOpen, engine.viewMode]);

  // ── Handlers ───────────────────────────────────────────────────────────

  const handlePlaySummary = useCallback(
    (flow: TourFlow) => {
      if (summaryPlaying) {
        tts.stop();
        setSummaryPlaying(false);
        playingFlowRef.current = null;
        return;
      }
      setSelectedFlow(flow);
      setSummaryPlaying(true);
      playingFlowRef.current = flow.id;
      // Trigger playback after state updates
      setTimeout(() => tts.play(), 50);
    },
    [summaryPlaying, tts],
  );

  const handleBack = useCallback(() => {
    tts.stop();
    setSummaryPlaying(false);
    playingFlowRef.current = null;
    setSelectedFlow(null);
  }, [tts]);

  const handleClose = useCallback(() => {
    tts.stop();
    setSummaryPlaying(false);
    playingFlowRef.current = null;
    setSelectedFlow(null);
    closeTour();
  }, [tts, closeTour]);

  const handleShowMe = useCallback(
    (flow: TourFlow) => {
      tts.stop();
      setSummaryPlaying(false);
      playingFlowRef.current = null;
      setSelectedFlow(null);
      openTour({ flowId: flow.id, format: 'interactive' });
    },
    [tts, openTour],
  );

  const handleContinue = useCallback(
    (flow: TourFlow, stepIndex: number) => {
      tts.stop();
      setSummaryPlaying(false);
      playingFlowRef.current = null;
      setSelectedFlow(null);
      // Find the step ID at the saved index
      const stepId = flow.steps[stepIndex]?.id;
      openTour({ flowId: flow.id, format: 'interactive', stepId });
    },
    [tts, openTour],
  );

  const handleChat = useCallback(
    (flow: TourFlow) => {
      tts.stop();
      setSummaryPlaying(false);
      playingFlowRef.current = null;
      setSelectedFlow(null);
      engine.selectChat(flow.id);
    },
    [tts, engine],
  );

  const handleModuleChat = useCallback(() => {
    tts.stop();
    setSummaryPlaying(false);
    playingFlowRef.current = null;
    setSelectedFlow(null);
    engine.selectChat();
  }, [tts, engine]);

  // ── Render guards ───────────────────────────────────────────────────────
  // Only render when the tour is open and in index mode
  if (!tour.isOpen || engine.viewMode !== 'index' || engine.isMinimized) return null;

  const primary = moduleColor;
  const secondary = moduleColor;

  // Check if a flow has saved progress
  const getSavedProgress = (flow: TourFlow): number => {
    if (progress.progress.last_active_flow_id === flow.id) {
      return progress.progress.last_active_step_index ?? 0;
    }
    return 0;
  };

  const hasSavedProgress = (flow: TourFlow): boolean => {
    const saved = getSavedProgress(flow);
    return saved > 0 && saved < flow.steps.length;
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col overflow-hidden bg-slate-950/65 backdrop-blur-sm"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div className="fixed inset-0" onClick={handleClose} aria-hidden="true" />

      <div
        className="relative z-10 grid h-full w-full flex-1 overflow-hidden lg:my-auto lg:h-[90vh] lg:max-w-6xl lg:rounded-[2rem] lg:border lg:shadow-2xl lg:grid-cols-[1.2fr_0.8fr]"
        style={{
          backgroundColor: isDark ? '#0f172a' : '#ffffff',
          borderColor: `${primary}2A`,
        }}
      >
        <div
          className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6 lg:p-10"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          {/* Header */}
          <div className="mb-6 flex items-start justify-between gap-4">
            <div className="flex-1">
              <div
                className="mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold"
                style={{ backgroundColor: `${primary}18`, color: primary }}
              >
                <Sparkles className="h-4 w-4" />
                {selectedFlow ? selectedFlow.label : `${moduleLabel} coach`}
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white sm:text-3xl">
                {selectedFlow ? selectedFlow.label : 'Welcome 👋'}
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300 sm:text-base">
                {selectedFlow
                  ? selectedFlow.view_summary ?? selectedFlow.summary
                  : `Welcome to the ${moduleLabel} module. Here's a quick overview of what you can explore. Pick a tour to get started, or chat with me if you have questions.`}
              </p>
            </div>

            {/* Close + Minimize — visible on mobile */}
            <div className="flex shrink-0 items-center gap-2 lg:hidden">
              <button
                type="button"
                onClick={engine.minimize}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border transition hover:scale-[1.02]"
                style={{ borderColor: `${primary}22`, color: isDark ? '#f8fafc' : '#0f172a' }}
                aria-label="Minimize"
                title="Minimize"
              >
                <Minimize2 className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border transition hover:scale-[1.02]"
                style={{ borderColor: `${primary}22`, color: isDark ? '#f8fafc' : '#0f172a' }}
                aria-label="Close"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Flow grid (when no flow is selected) */}
          {!selectedFlow && (
            <>
              {flows.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  No tours available for this module yet.
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {flows.map((flow) => {
                    const interactiveSteps = flow.steps.filter(
                      (s) => s.verification?.event_type || s.verification?.target_selector,
                    ).length;
                    const hasAudio = flow.steps.some((s) => s.audio_url || s.scripted_description);
                    return (
                      <button
                        key={flow.id}
                        type="button"
                        onClick={() => setSelectedFlow(flow)}
                        className="rounded-2xl border p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-lg"
                        style={{
                          backgroundColor: isDark ? 'rgba(15, 23, 42, 0.65)' : '#f8fafc',
                          borderColor: `${primary}16`,
                        }}
                      >
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <span
                            className="inline-flex h-10 w-10 items-center justify-center rounded-2xl"
                            style={{
                              background: `linear-gradient(135deg, ${primary}, ${secondary})`,
                              color: '#ffffff',
                            }}
                          >
                            <Sparkles className="h-5 w-5" />
                          </span>
                          {interactiveSteps > 0 && (
                            <span
                              className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                              style={{ backgroundColor: `${secondary}18`, color: secondary }}
                            >
                              Interactive
                            </span>
                          )}
                        </div>
                        <p className="font-semibold text-slate-900 dark:text-white">{flow.label}</p>
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                          {flow.steps.length} step{flow.steps.length !== 1 ? 's' : ''}
                          {interactiveSteps > 0 ? ` · ${interactiveSteps} interactive` : ''}
                        </p>
                        {hasAudio && (
                          <span className="mt-2 inline-block rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            Audio ready
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Chat option */}
              {flows.length > 0 && (
                <div className="mt-6 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleModuleChat}
                    className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition hover:bg-slate-50 dark:hover:bg-slate-800"
                    style={{ borderColor: `${primary}24`, color: isDark ? '#f8fafc' : '#0f172a' }}
                  >
                    <MessageSquare className="h-4 w-4" />
                    Just chat with me
                  </button>
                </div>
              )}
            </>
          )}

          {/* Detail view (when a flow is selected) */}
          {selectedFlow && (
            <div
              className="rounded-[1.75rem] border p-6"
              style={{
                backgroundColor: isDark ? 'rgba(15, 23, 42, 0.78)' : '#ffffff',
                borderColor: `${primary}24`,
              }}
            >
              {/* Back button */}
              <button
                type="button"
                onClick={handleBack}
                className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              >
                <ArrowLeft className="h-4 w-4" />
                All tours
              </button>

              {/* Summary with TTS */}
              <div className="mb-4 flex items-start gap-3">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                    {selectedFlow.label}
                  </h3>
                  <p className="mt-2 text-base leading-7 text-slate-600 dark:text-slate-300">
                    {selectedFlow.view_summary ?? selectedFlow.summary}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handlePlaySummary(selectedFlow)}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition hover:scale-[1.02]"
                  style={{ borderColor: `${primary}24`, color: primary }}
                  aria-label={summaryPlaying ? 'Stop narration' : 'Play narration'}
                  title={summaryPlaying ? 'Stop narration' : 'Play narration'}
                >
                  {summaryPlaying ? <X className="h-4 w-4" /> : <Volume2 className="h-5 w-5" />}
                </button>
              </div>

              {/* Tour info badges */}
              <div className="mb-6 flex flex-wrap gap-2">
                <span
                  className="rounded-full px-3 py-1 text-xs font-semibold"
                  style={{ backgroundColor: `${primary}14`, color: primary }}
                >
                  {selectedFlow.steps.length} steps
                </span>
                {selectedFlow.section_prompt && (
                  <button
                    type="button"
                    onClick={() => handleChat(selectedFlow)}
                    className="rounded-full border px-3 py-1 text-xs font-semibold transition hover:bg-slate-50 dark:hover:bg-slate-800"
                    style={{ borderColor: `${primary}24`, color: isDark ? '#f8fafc' : '#0f172a' }}
                  >
                    Chat available
                  </button>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                {hasSavedProgress(selectedFlow) && (
                  <button
                    type="button"
                    onClick={() => handleContinue(selectedFlow, getSavedProgress(selectedFlow))}
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                    style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}
                  >
                    <Play className="h-4 w-4" />
                    Continue from step {getSavedProgress(selectedFlow) + 1}
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => handleShowMe(selectedFlow)}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                  style={{
                    background: hasSavedProgress(selectedFlow)
                      ? `linear-gradient(135deg, ${primary}AA, ${secondary}AA)`
                      : `linear-gradient(135deg, ${primary}, ${secondary})`,
                  }}
                >
                  {hasSavedProgress(selectedFlow) && <RotateCcw className="h-4 w-4" />}
                  {hasSavedProgress(selectedFlow) ? 'Start from beginning' : 'Show me now'}
                  {!hasSavedProgress(selectedFlow) && <ChevronRight className="h-4 w-4" />}
                </button>

                {selectedFlow.section_prompt && (
                  <button
                    type="button"
                    onClick={() => handleChat(selectedFlow)}
                    className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold transition hover:bg-slate-50 dark:hover:bg-slate-800"
                    style={{ borderColor: `${primary}24`, color: isDark ? '#f8fafc' : '#0f172a' }}
                  >
                    <MessageSquare className="h-4 w-4" />
                    Let's chat about it
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right side: mascot panel (desktop only) */}
        <div
          className="relative hidden min-h-0 overflow-hidden lg:block"
          style={{ background: `linear-gradient(180deg, ${primary}12, ${secondary}16)` }}
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.38),transparent_42%)]" />

          {/* Close + Minimize buttons — top-right */}
          <div className="absolute right-4 top-4 z-10 flex items-center gap-2">
            <button
              type="button"
              onClick={engine.minimize}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border transition hover:scale-[1.02]"
              style={{
                borderColor: `${primary}22`,
                backgroundColor: isDark ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.7)',
                color: isDark ? '#f8fafc' : '#0f172a',
              }}
              aria-label="Minimize"
              title="Minimize"
            >
              <Minimize2 className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border transition hover:scale-[1.02]"
              style={{
                borderColor: `${primary}22`,
                backgroundColor: isDark ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.7)',
                color: isDark ? '#f8fafc' : '#0f172a',
              }}
              aria-label="Close"
              title="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* TTS toggle — top-left */}
          <button
            type="button"
            onClick={tts.toggleEnabled}
            className="absolute left-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/70 text-slate-400 transition hover:scale-[1.02] dark:border-slate-600 dark:bg-slate-700/60 dark:text-slate-300"
            style={
              tts.enabled
                ? {
                    borderColor: `${primary}66`,
                    backgroundColor: isDark ? `${primary}33` : `${primary}26`,
                    color: primary,
                  }
                : undefined
            }
            aria-label={tts.enabled ? 'Mute coach voice' : 'Enable coach voice'}
            title={tts.enabled ? 'Mute coach voice' : 'Enable coach voice'}
          >
            {tts.enabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
          </button>

          <div className="relative flex h-full flex-col justify-between p-10">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300">
                Your guide
              </p>
              <h3 className="mt-3 text-2xl font-bold text-slate-900 dark:text-white">
                {selectedFlow
                  ? selectedFlow.label
                  : 'A quick orientation before the real work begins.'}
              </h3>
              <p className="mt-4 text-base leading-7 text-slate-600 dark:text-slate-300">
                {selectedFlow
                  ? (selectedFlow.view_summary ?? selectedFlow.summary)
                  : "Pick a tour below and I'll walk you through it step by step. You can choose a guided tour or just chat if you have questions."}
              </p>
            </div>

            <div className="flex items-end justify-center py-8">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={FALLBACK_MASCOT_URL}
                alt="Coach mascot"
                className="h-auto max-h-[420px] w-auto object-contain drop-shadow-[0_25px_45px_rgba(15,23,42,0.24)]"
              />
            </div>

            <div
              className="rounded-[1.5rem] border p-5"
              style={{
                backgroundColor: isDark ? 'rgba(15, 23, 42, 0.78)' : 'rgba(255, 255, 255, 0.72)',
                borderColor: `${primary}28`,
              }}
            >
              <p className="text-sm font-semibold" style={{ color: primary }}>
                {selectedFlow ? 'Ready when you are' : 'Suggested next move'}
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                {selectedFlow
                  ? "Choose 'Show me now' for a guided tour, or 'Let's chat' if you have questions."
                  : "Pick a tour to get started, or chat with me if you're not sure where to begin."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
